import { Upload } from "lucide-react";
import { FormattedMessage } from "react-intl";

import { FC } from "react";
import { ConditionalTooltip } from "~/components/ConditionalTooltip";
import { Button } from "~/components/ui/button";
import { trackEvent } from "~/utils/analytics";
import { UploadDocumentsDialog } from "./UploadDocumentsDialog";

type BaseUploadButtonProps = {
  isModalOpen: boolean;
  setIsModalOpen: (isOpen: boolean) => void;
  onFilesSelected: (files: File[]) => void;
  disabled?: boolean;
};

type CaseUploadButtonProps = BaseUploadButtonProps & {
  isKnowledge?: false;
  caseId: string;
  organizationId: string;
  autoCloseModal: boolean;
};

type KnowledgeUploadButtonProps = BaseUploadButtonProps & {
  isKnowledge: true;
  organizationId?: string;
  caseId?: never;
  autoCloseModal: boolean;
};

type UploadButtonProps = CaseUploadButtonProps | KnowledgeUploadButtonProps;

export const UploadButton: FC<UploadButtonProps> = ({
  isModalOpen,
  setIsModalOpen,
  onFilesSelected,
  autoCloseModal,
  disabled,
  ...contextProps
}) => {
  const isKnowledge = contextProps.isKnowledge ?? false;
  const caseId = "caseId" in contextProps ? contextProps.caseId : undefined;
  const organizationId =
    "organizationId" in contextProps ? contextProps.organizationId : undefined;
  const onUploadButtonClick = () => {
    setIsModalOpen(true);
  };

  return (
    <>
      <ConditionalTooltip condition={disabled}>
        <Button
          onClick={() => {
            trackEvent({ eventName: "app/case/upload.button/click" });
            onUploadButtonClick();
          }}
          variant={isKnowledge ? "default" : "ghost"}
          className={!isKnowledge ? "text-blue-600 hover:text-blue-700" : ""}
          disabled={disabled}
        >
          <Upload className="h-4 w-4 mr-1" />
          <FormattedMessage
            id="documentsList.upload"
            defaultMessage={"Upload"}
          />
        </Button>
      </ConditionalTooltip>
      <UploadDocumentsDialog
        autoCloseModal={autoCloseModal}
        isOpen={isModalOpen}
        setIsOpen={setIsModalOpen}
        onDrop={onFilesSelected}
        {...(isKnowledge
          ? { isKnowledge: true, organizationId }
          : {
              isKnowledge: false,
              caseId: caseId!,
              organizationId: organizationId!,
            })}
      />
    </>
  );
};
